import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PageLoader from '../../../../../components/other/PageLoader';
import { ReactComponent as TaskIcon } from '../../../../../assets/images/task-icon.svg';

const TASKS_TREE = 'tasks';

// A form is placed either in the corporate library or on the Tasks page, and the
// two number their folders apart: the same id is a different folder in each, so a
// folder is only ever identified by the pair.
const folderKey = (tree, id) => `${tree ?? 'library'}:${id ?? 'root'}`;

/**
 * Folder-by-folder picker for the forms of a task, mirroring the "Select forms"
 * browser of the statistics report.
 *
 * The forms arrive already filtered by the parent: once the first form is
 * picked, only the ones whose settings match it remain. Folders are derived
 * from that filtered list, so a folder shows up only while it still holds at
 * least one selectable form somewhere in its subtree. Folders are navigated
 * into, never checked as a whole - a folder may mix forms with different
 * settings, and those cannot share a task.
 *
 * Both trees are browsed here. The Tasks page opens on the root level, below the
 * library rows and behind a separator, the way it does everywhere else.
 */
const FormsFolderBrowser = ({ forms = [], folders = [], rootFolder = null, search = '', isLoading = false, onSelectForm }) => {
  const rootFolderId = rootFolder?.id ?? null;
  const libraryRootKey = folderKey(null, rootFolderId);
  const tasksRootKey = folderKey(TASKS_TREE, null);

  const [currentFolder, setCurrentFolder] = useState({ id: rootFolderId, tree: null });
  const currentKey = folderKey(currentFolder.tree, currentFolder.id);
  const isAtLibraryRoot = currentKey === libraryRootKey;

  const isSearchMode = search.trim().length > 0;

  useEffect(() => {
    setCurrentFolder({ id: rootFolderId, tree: null });
  }, [rootFolderId]);

  const foldersByKey = useMemo(() => new Map(folders.map((folder) => [folderKey(folder.tree, folder.id), folder])), [folders]);

  // The root of a tree, as the level standing for it is keyed. The library root is a
  // folder of its own, the Tasks one is the section this browser shows it under.
  const rootKeyFor = useCallback((tree) => (tree === TASKS_TREE ? tasksRootKey : libraryRootKey), [tasksRootKey, libraryRootKey]);

  const parentKeyOf = useCallback(
    (folder) => (folder.parent_id ? folderKey(folder.tree, folder.parent_id) : rootKeyFor(folder.tree)),
    [rootKeyFor],
  );

  // Forms sitting directly in a folder. Anything whose folder is unknown (or
  // absent) is shown at the root of its own tree so it can never become unreachable.
  const formsByFolderKey = useMemo(() => {
    const map = new Map();

    forms.forEach((form) => {
      const ownKey = folderKey(form.tree, form.folder_id);
      const key = foldersByKey.has(ownKey) ? ownKey : rootKeyFor(form.tree);
      const folderForms = map.get(key);

      if (folderForms) {
        folderForms.push(form);
      } else {
        map.set(key, [form]);
      }
    });

    return map;
  }, [forms, foldersByKey, rootKeyFor]);

  // A folder is worth showing when it - or any of its descendants - holds a
  // form the user may still add to the task.
  const foldersWithForms = useMemo(() => {
    const result = new Set();

    formsByFolderKey.forEach((_, key) => {
      let folder = foldersByKey.get(key);

      while (folder && !result.has(folderKey(folder.tree, folder.id))) {
        result.add(folderKey(folder.tree, folder.id));
        folder = foldersByKey.get(parentKeyOf(folder));
      }
    });

    return result;
  }, [formsByFolderKey, foldersByKey, parentKeyOf]);

  // Selecting a form narrows the tree, so the folder we are standing in can
  // stop matching: step back to the root instead of showing a dead end.
  useEffect(() => {
    if (!isAtLibraryRoot && !foldersWithForms.has(currentKey)) {
      setCurrentFolder({ id: rootFolderId, tree: null });
    }
  }, [currentKey, isAtLibraryRoot, rootFolderId, foldersWithForms]);

  const childFoldersOf = useCallback(
    (parentKey) => folders.filter((folder) => parentKeyOf(folder) === parentKey && foldersWithForms.has(folderKey(folder.tree, folder.id))),
    [folders, foldersWithForms, parentKeyOf],
  );

  const childFolders = useMemo(() => childFoldersOf(currentKey), [childFoldersOf, currentKey]);
  const displayForms = useMemo(() => {
    if (isSearchMode) {
      const query = search.trim().toLowerCase();

      return forms.filter((form) => form.tree !== TASKS_TREE && form.name?.toLowerCase().includes(query));
    }

    return formsByFolderKey.get(currentKey) || [];
  }, [isSearchMode, search, forms, formsByFolderKey, currentKey]);

  // The Tasks page is offered on the root level only -- below it, the level shown
  // belongs to one tree and there is nothing to separate it from.
  const tasksFolders = useMemo(
    () => (isAtLibraryRoot && !isSearchMode ? childFoldersOf(tasksRootKey) : []),
    [isAtLibraryRoot, isSearchMode, childFoldersOf, tasksRootKey],
  );
  const tasksForms = useMemo(() => {
    if (isSearchMode) {
      const query = search.trim().toLowerCase();

      return forms.filter((form) => form.tree === TASKS_TREE && form.name?.toLowerCase().includes(query));
    }

    return isAtLibraryRoot ? formsByFolderKey.get(tasksRootKey) || [] : [];
  }, [isSearchMode, search, forms, isAtLibraryRoot, formsByFolderKey, tasksRootKey]);
  const hasTasksItems = tasksFolders.length > 0 || tasksForms.length > 0;

  const breadcrumbs = useMemo(() => {
    const trail = [];
    let folder = foldersByKey.get(currentKey);

    while (folder) {
      trail.unshift({ id: folder.id, tree: folder.tree ?? null, name: folder.name });
      folder = foldersByKey.get(parentKeyOf(folder));
    }

    if (rootFolder) {
      // The level the Tasks page opens at, so it heads the trail of both trees.
      trail.unshift({ id: rootFolder.id, tree: null, name: rootFolder.name });
    }

    return trail;
  }, [currentKey, foldersByKey, parentKeyOf, rootFolder]);

  const renderBreadcrumbs = () => {
    if (isSearchMode || !breadcrumbs.length) {
      return null;
    }

    return (
      <div className="forms-modal-breadcrumbs">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={folderKey(crumb.tree, crumb.id)}>
            {index > 0 && <span className="breadcrumb-separator">{'>'}</span>}
            <button
              type="button"
              className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? 'breadcrumb-item--active' : ''}`}
              onClick={() => setCurrentFolder({ id: crumb.id, tree: crumb.tree })}
              disabled={index === breadcrumbs.length - 1}
            >
              {crumb.name}
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderFolders = (folderRows) => {
    if (isSearchMode) {
      return null;
    }

    return folderRows.map((folder) => (
      <div key={`folder-${folderKey(folder.tree, folder.id)}`} className="forms-modal-folder-row">
        <div className="forms-modal-folder-clickable" onClick={() => setCurrentFolder({ id: folder.id, tree: folder.tree ?? null })}>
          <img src="/images/folder-big-icon.png" className="forms-modal-row-icon" alt="" />
          <span className="forms-modal-folder-name">{folder.name}</span>
          <svg
            className="forms-modal-folder-chevron"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M7.5 15L12.5 10L7.5 5" stroke="#667085" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
    ));
  };

  const renderForms = (formRows) =>
    formRows.map((form) => (
      <div key={`form-${form.id}`} className="forms-modal-form-row">
        <label className="form-control-checkbox forms-modal-form-checkbox" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={!!form.checked} onChange={() => onSelectForm(form)} />
          <span></span>
        </label>
        <div className="forms-modal-form-clickable" onClick={() => onSelectForm(form)}>
          <img src="/images/file-big-icon.png" className="forms-modal-row-icon" alt="" />
          <span className="forms-modal-form-name">{form.name}</span>
        </div>
      </div>
    ));

  const renderEmptyState = () => {
    if (isLoading || displayForms.length || childFolders.length || hasTasksItems) {
      return null;
    }

    return <div className="no--result">{isSearchMode ? 'No forms found for your search.' : 'No forms in this folder.'}</div>;
  };

  return (
    <div className="forms--suggest">
      {isLoading && <PageLoader />}
      {renderBreadcrumbs()}
      <div className="forms--suggest__list">
        {renderFolders(childFolders)}
        {renderForms(displayForms)}

        {hasTasksItems && (
          <>
            {/* The line the Tasks page starts at: everything below it is placed
                there, not among the library folders above. */}
            <div className="forms-modal-tasks-separator">
              <TaskIcon />
              Tasks
            </div>
            {renderFolders(tasksFolders)}
            {renderForms(tasksForms)}
          </>
        )}

        {renderEmptyState()}
      </div>
    </div>
  );
};

export default memo(FormsFolderBrowser);
